import { useState, useEffect, useRef } from 'react';
import { useNotifications } from '../contexts/NotificationContext';
import Modal from './shared/Modal';

export default function NotificationDropdown({ align = 'right' }) {
  const {
    notifications,
    unreadCount,
    markAsRead,
    deleteNotification,
    clearAll,
    fetchNotifications,
    showModal
  } = useNotifications();

  const [isOpen, setIsOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isViewAllModalOpen, setIsViewAllModalOpen] = useState(false);
  const [expandedNotifications, setExpandedNotifications] = useState(new Set());
  const [severityFilter, setSeverityFilter] = useState('all');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Refresh notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      handleRefresh();
    }
  }, [isOpen]);

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (notification.status === 'unread') {
      await markAsRead(notification.id);
    }

    // If requires action, show the modal
    if (notification.requires_action) {
      showModal(notification);
      setIsOpen(false);
    } else {
      // Otherwise show detail modal
      setSelectedNotification(notification);
      setIsDetailModalOpen(true);
    }
  };

  const handleViewDetails = (e, notification) => {
    e.stopPropagation();
    setSelectedNotification(notification);
    setIsDetailModalOpen(true);
    if (notification.status === 'unread') {
      markAsRead(notification.id);
    }
  };

  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedNotification(null);
  };

  const handleRefresh = async () => {
    // Fetch notifications (unread count is automatically recalculated)
    await fetchNotifications({ limit: 50 });
  };

  const handleClearAll = async () => {
    if (notifications.length === 0) return;
    if (window.confirm('Are you sure you want to clear all notifications?')) {
      await clearAll();
    }
  };

  const toggleNotificationExpanded = (notificationId) => {
    setExpandedNotifications(prev => {
      const newSet = new Set(prev);
      if (newSet.has(notificationId)) {
        newSet.delete(notificationId);
      } else {
        newSet.add(notificationId);
      }
      return newSet;
    });
  };

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'error':
        return 'border-l-4 border-red-500 bg-red-900/20';
      case 'warning':
        return 'border-l-4 border-orange-500 bg-orange-900/20';
      case 'success':
        return 'border-l-4 border-green-500 bg-green-900/20';
      case 'info':
      default:
        return 'border-l-4 border-blue-500 bg-blue-900/20';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'error':
        return (
          <svg className="w-5 h-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'success':
        return (
          <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg className="w-5 h-5 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationButtonStyles = () => {
    if (notifications.length === 0 || unreadCount === 0) {
      // Default style when no notifications or all read - match other buttons
      return 'border-transparent';
    }

    // Get the most recent unread notification's severity
    const mostRecentUnread = notifications.find(n => n.status === 'unread');
    const mostRecentSeverity = mostRecentUnread?.severity;

    switch (mostRecentSeverity) {
      case 'error':
        return 'text-red-400 border-transparent';
      case 'warning':
        return 'text-orange-400 border-transparent';
      case 'success':
        return 'text-green-400 border-transparent';
      case 'info':
      default:
        return 'text-blue-400 border-transparent';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-lg border transition-all duration-200 ${getNotificationButtonStyles()}`}
        style={{
          color: getNotificationButtonStyles().includes('text-') ? undefined : 'var(--color-text-muted)',
          backgroundColor: 'transparent'
        }}
        onMouseEnter={(e) => {
          if (!getNotificationButtonStyles().includes('text-')) {
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }
          e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
        }}
        onMouseLeave={(e) => {
          if (!getNotificationButtonStyles().includes('text-')) {
            e.currentTarget.style.color = 'var(--color-text-muted)';
          }
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
        title="Notifications"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold" style={{ color: 'var(--color-text-on-accent)' }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Mobile backdrop */}
          <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={() => setIsOpen(false)} />

          <div
            className={`fixed md:absolute ${align === 'left' ? 'left-0' : 'right-0'} top-0 md:top-auto md:mt-2 w-full md:w-96 h-full md:h-auto border-0 md:border md:rounded-xl shadow-2xl z-50 overflow-hidden`}
            style={{
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-border-default)'
            }}
          >
            {/* Caret pointing to button - desktop only */}
            <div
              className={`hidden md:block absolute -top-2 ${align === 'left' ? 'left-3' : 'right-3'} w-4 h-4 border-l border-t transform rotate-45`}
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                borderColor: 'var(--color-border-default)'
              }}
            ></div>

            {/* Header */}
            <div
              className="relative px-4 py-4 md:py-3 border-b flex items-center justify-between"
              style={{
                background: 'linear-gradient(135deg, var(--color-bg-tertiary), var(--color-bg-secondary))',
                borderColor: 'var(--color-border-default)'
              }}
            >
              <h3 className="text-base md:text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>Notifications</h3>
              <div className="flex items-center space-x-2 md:space-x-3">
                {unreadCount > 0 && (
                  <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{unreadCount} unread</span>
                )}
                {/* Refresh button */}
                <button
                  onClick={handleRefresh}
                  className="p-1.5 md:p-1 rounded transition-colors"
                  style={{ color: 'var(--color-text-muted)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--color-text-primary)';
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--color-text-muted)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                  title="Refresh notifications"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                {/* Clear all button */}
                {notifications.length > 0 && (
                  <button
                    onClick={handleClearAll}
                    className="p-1.5 md:p-1 rounded transition-colors"
                    style={{ color: 'var(--color-text-muted)' }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#ef4444';
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = 'var(--color-text-muted)';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    title="Clear all notifications"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
                {/* Close button - mobile only */}
                <button
                  onClick={() => setIsOpen(false)}
                  className="md:hidden p-1 rounded transition-colors"
                  style={{ color: 'var(--color-text-muted)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = 'var(--color-text-primary)';
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--color-text-muted)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Notifications List */}
            <div className="h-[calc(100vh-4rem)] md:h-auto md:max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center" style={{ color: 'var(--color-text-muted)' }}>
                <svg className="w-12 h-12 mx-auto mb-2" style={{ color: 'var(--color-text-disabled)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <p className="text-sm">No notifications</p>
              </div>
            ) : (
              <div className="divide-y" style={{ borderColor: 'var(--color-border-default)' }}>
                {notifications.map((notification) => {
                  const itemId = `notification-${notification.id}`;
                  return (
                  <div
                    key={notification.id}
                    id={itemId}
                    onClick={() => notification.requires_action ? handleNotificationClick(notification) : null}
                    className={`relative group p-4 md:p-3 ${getSeverityStyles(notification.severity)} transition-colors ${notification.requires_action ? 'cursor-pointer' : ''}`}
                    style={{
                      backgroundColor: notification.status === 'unread' ? 'var(--color-bg-hover)' : 'transparent'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = notification.status === 'unread' ? 'var(--color-bg-hover)' : 'transparent';
                    }}
                  >
                    <div className="flex items-start space-x-3">
                      {/* Timestamp - top right */}
                      <span className="absolute top-3 right-3 text-xs" style={{ color: 'var(--color-text-disabled)' }}>
                        {formatTimestamp(notification.created_at)}
                      </span>

                      {/* Icon */}
                      <div className="flex-shrink-0">
                        {getSeverityIcon(notification.severity)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Title */}
                        <h4 className="text-sm md:text-sm font-medium line-clamp-2 md:truncate pr-16" style={{ color: 'var(--color-text-primary)' }} title={notification.title}>
                          {notification.title}
                        </h4>

                        {/* Message */}
                        <p className="mt-1 md:mt-0.5 text-sm md:text-xs line-clamp-2 md:line-clamp-1" style={{ color: 'var(--color-text-muted)' }} title={notification.message}>
                          {notification.message}
                        </p>

                        {/* Actions */}
                        <div className="mt-3 md:mt-2 flex items-center space-x-3">
                          {notification.requires_action ? (
                            <button
                              onClick={() => handleNotificationClick(notification)}
                              className="text-sm md:text-xs text-purple-400 hover:text-purple-300 font-medium transition-colors py-1 md:py-0"
                            >
                              Click to resolve →
                            </button>
                          ) : (
                            <button
                              onClick={(e) => handleViewDetails(e, notification)}
                              className="text-sm md:text-xs text-blue-400 hover:text-blue-300 transition-colors py-1 md:py-0"
                            >
                              View details
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer - View All */}
          {notifications.length > 0 && (
            <div
              className="px-4 py-4 md:py-3 border-t"
              style={{
                background: 'linear-gradient(135deg, var(--color-bg-tertiary), var(--color-bg-secondary))',
                borderColor: 'var(--color-border-default)'
              }}
            >
              <button
                onClick={() => {
                  setIsOpen(false);
                  setIsViewAllModalOpen(true);
                }}
                className="w-full text-center text-sm text-purple-400 hover:text-purple-300 active:text-purple-500 transition-colors font-medium py-2 md:py-0"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
        </>
      )}

      {/* Detail Modal */}
      {selectedNotification && (
        <Modal
          isOpen={isDetailModalOpen}
          onClose={closeDetailModal}
          title={selectedNotification.title}
          size="md"
          zIndex={10000}
        >
          <div className="space-y-4">
            {/* Severity Badge */}
            <div className="flex items-center space-x-2">
              {getSeverityIcon(selectedNotification.severity)}
              <span className={`text-sm font-medium ${
                selectedNotification.severity === 'error' ? 'text-red-400' :
                selectedNotification.severity === 'warning' ? 'text-orange-400' :
                selectedNotification.severity === 'success' ? 'text-green-400' :
                'text-blue-400'
              }`}>
                {selectedNotification.severity.charAt(0).toUpperCase() + selectedNotification.severity.slice(1)}
              </span>
              <span className="text-xs text-gray-500">•</span>
              <span className="text-xs text-gray-400">
                {formatTimestamp(selectedNotification.created_at)}
              </span>
            </div>

            {/* Message */}
            <div
              className="rounded-lg p-4 border"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                borderColor: 'var(--color-border-default)'
              }}
            >
              <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--color-text-secondary)' }}>
                {selectedNotification.message}
              </p>
            </div>

            {/* Additional Data */}
            {selectedNotification.data && Object.keys(selectedNotification.data).length > 0 && (
              <div
                className="rounded-lg p-4 border"
                style={{
                  backgroundColor: 'var(--color-bg-tertiary)',
                  borderColor: 'var(--color-border-default)'
                }}
              >
                <h4 className="text-xs font-semibold mb-2" style={{ color: 'var(--color-text-muted)' }}>Additional Information</h4>
                <dl className="space-y-2">
                  {Object.entries(selectedNotification.data).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-xs">
                      <dt style={{ color: 'var(--color-text-disabled)' }}>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</dt>
                      <dd className="font-mono" style={{ color: 'var(--color-text-secondary)' }}>{String(value)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-2">
              {selectedNotification.requires_action && (
                <button
                  onClick={() => {
                    closeDetailModal();
                    handleNotificationClick(selectedNotification);
                  }}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-sm font-medium transition-colors"
                  style={{ color: 'var(--color-text-on-accent)' }}
                >
                  Resolve Issue
                </button>
              )}
              <button
                onClick={closeDetailModal}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{
                  backgroundColor: 'var(--color-bg-tertiary)',
                  color: 'var(--color-text-primary)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* View All Notifications Modal */}
      <Modal
        isOpen={isViewAllModalOpen}
        onClose={() => {
          setIsViewAllModalOpen(false);
          setSeverityFilter('all');
        }}
        title="All Notifications"
        size="lg"
        zIndex={10000}
      >
        {/* Severity Filter Buttons */}
        <div className="flex items-center space-x-2 mb-4 pb-4 border-b" style={{ borderColor: 'var(--color-border-default)' }}>
          <span className="text-sm mr-2" style={{ color: 'var(--color-text-muted)' }}>Filter:</span>
          <button
            onClick={() => setSeverityFilter('all')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              severityFilter === 'all' ? '' : ''
            }`}
            style={{
              backgroundColor: severityFilter === 'all' ? 'var(--color-bg-tertiary)' : 'var(--color-bg-secondary)',
              color: severityFilter === 'all' ? 'var(--color-text-primary)' : 'var(--color-text-muted)'
            }}
            onMouseEnter={(e) => {
              if (severityFilter !== 'all') {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                e.currentTarget.style.color = 'var(--color-text-secondary)';
              }
            }}
            onMouseLeave={(e) => {
              if (severityFilter !== 'all') {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                e.currentTarget.style.color = 'var(--color-text-muted)';
              }
            }}
          >
            All
          </button>
          <button
            onClick={() => setSeverityFilter('error')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              severityFilter === 'error'
                ? 'bg-red-600 text-white'
                : 'text-red-400'
            }`}
            style={severityFilter !== 'error' ? { backgroundColor: 'var(--color-bg-secondary)' } : {}}
            onMouseEnter={(e) => {
              if (severityFilter !== 'error') {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
              }
            }}
            onMouseLeave={(e) => {
              if (severityFilter !== 'error') {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }
            }}
          >
            Error
          </button>
          <button
            onClick={() => setSeverityFilter('warning')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              severityFilter === 'warning'
                ? 'bg-orange-600 text-white'
                : 'text-orange-400'
            }`}
            style={severityFilter !== 'warning' ? { backgroundColor: 'var(--color-bg-secondary)' } : {}}
            onMouseEnter={(e) => {
              if (severityFilter !== 'warning') {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
              }
            }}
            onMouseLeave={(e) => {
              if (severityFilter !== 'warning') {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }
            }}
          >
            Warning
          </button>
          <button
            onClick={() => setSeverityFilter('info')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              severityFilter === 'info'
                ? 'bg-blue-600 text-white'
                : 'text-blue-400'
            }`}
            style={severityFilter !== 'info' ? { backgroundColor: 'var(--color-bg-secondary)' } : {}}
            onMouseEnter={(e) => {
              if (severityFilter !== 'info') {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
              }
            }}
            onMouseLeave={(e) => {
              if (severityFilter !== 'info') {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }
            }}
          >
            Info
          </button>
          <button
            onClick={() => setSeverityFilter('success')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              severityFilter === 'success'
                ? 'bg-green-600 text-white'
                : 'text-green-400'
            }`}
            style={severityFilter !== 'success' ? { backgroundColor: 'var(--color-bg-secondary)' } : {}}
            onMouseEnter={(e) => {
              if (severityFilter !== 'success') {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
              }
            }}
            onMouseLeave={(e) => {
              if (severityFilter !== 'success') {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
              }
            }}
          >
            Success
          </button>
        </div>

        <div className="space-y-2">
          {(() => {
            const filteredNotifications = severityFilter === 'all'
              ? notifications
              : notifications.filter(n => n.severity === severityFilter);

            if (filteredNotifications.length === 0) {
              return (
                <div className="py-12 text-center" style={{ color: 'var(--color-text-muted)' }}>
                  <svg className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-text-disabled)' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                  <p className="text-base">
                    {notifications.length === 0 ? 'No notifications' : `No ${severityFilter} notifications`}
                  </p>
                </div>
              );
            }

            return filteredNotifications.map((notification) => {
              const isExpanded = expandedNotifications.has(notification.id);
              const itemBgId = `view-all-notification-${notification.id}`;
              return (
                <div
                  key={notification.id}
                  id={itemBgId}
                  className={`border rounded-lg ${getSeverityStyles(notification.severity)}`}
                  style={{
                    borderColor: 'var(--color-border-default)',
                    backgroundColor: notification.status === 'unread' ? 'var(--color-bg-hover)' : 'transparent'
                  }}
                >
                  {/* Collapsed Header */}
                  <div
                    onClick={() => toggleNotificationExpanded(notification.id)}
                    className="flex items-center space-x-3 p-3 cursor-pointer transition-colors rounded-lg"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    {/* Chevron */}
                    <div className="flex-shrink-0">
                      <svg
                        className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                        style={{ color: 'var(--color-text-muted)' }}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>

                    {/* Icon */}
                    <div className="flex-shrink-0">
                      {getSeverityIcon(notification.severity)}
                    </div>

                    {/* Title and badges */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className={`text-xs font-semibold uppercase ${
                          notification.severity === 'error' ? 'text-red-400' :
                          notification.severity === 'warning' ? 'text-orange-400' :
                          notification.severity === 'success' ? 'text-green-400' :
                          'text-blue-400'
                        }`}>
                          {notification.severity}
                        </span>
                        {notification.status === 'unread' && (
                          <span className="px-2 py-0.5 bg-purple-600 text-xs font-medium rounded" style={{ color: 'var(--color-text-on-accent)' }}>
                                                      New
                          </span>
                        )}
                      </div>
                      <h4 className="text-sm font-semibold truncate" style={{ color: 'var(--color-text-primary)' }}>
                        {notification.title}
                      </h4>
                    </div>

                    {/* Timestamp */}
                    <span className="text-xs flex-shrink-0" style={{ color: 'var(--color-text-disabled)' }}>
                      {formatTimestamp(notification.created_at)}
                    </span>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="px-3 pb-3 pt-0 space-y-3 border-t mt-2" style={{ borderColor: 'var(--color-border-default)' }}>
                      {/* Message */}
                      <div className="pl-8">
                        <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--color-text-secondary)' }}>
                          {notification.message}
                        </p>
                      </div>

                      {/* Additional Data */}
                      {notification.data && Object.keys(notification.data).length > 0 && (
                        <div className="pl-8">
                          <div
                            className="p-3 rounded border"
                            style={{
                              backgroundColor: 'var(--color-bg-tertiary)',
                              borderColor: 'var(--color-border-default)'
                            }}
                          >
                            <h5 className="text-xs font-semibold mb-2" style={{ color: 'var(--color-text-muted)' }}>Additional Information</h5>
                            <dl className="space-y-1">
                              {Object.entries(notification.data).map(([key, value]) => (
                                <div key={key} className="flex justify-between text-xs">
                                  <dt style={{ color: 'var(--color-text-disabled)' }}>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</dt>
                                  <dd className="font-mono" style={{ color: 'var(--color-text-secondary)' }}>{String(value)}</dd>
                                </div>
                              ))}
                            </dl>
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="pl-8 flex items-center space-x-3">
                        {notification.requires_action ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setIsViewAllModalOpen(false);
                              handleNotificationClick(notification);
                            }}
                            className="text-sm text-purple-400 hover:text-purple-300 font-medium transition-colors"
                          >
                            Click to resolve →
                          </button>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetails(e, notification);
                              setIsViewAllModalOpen(false);
                            }}
                            className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                          >
                            View details
                          </button>
                        )}
                        {notification.status === 'unread' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                            }}
                            className="text-sm transition-colors"
                            style={{ color: 'var(--color-text-muted)' }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.color = 'var(--color-text-secondary)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.color = 'var(--color-text-muted)';
                            }}
                          >
                            Mark as read
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>

        {/* Footer with actions */}
        {notifications.length > 0 && (
          <div className="mt-6 pt-4 border-t flex justify-between items-center" style={{ borderColor: 'var(--color-border-default)' }}>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2"
              style={{
                backgroundColor: 'var(--color-bg-tertiary)',
                color: 'var(--color-text-primary)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
              }}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>Refresh</span>
            </button>
            <button
              onClick={() => {
                handleClearAll();
                setIsViewAllModalOpen(false);
                setSeverityFilter('all');
              }}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
            >
              Clear All
            </button>
          </div>
        )}
      </Modal>
    </div>
  );
}
